import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { cn } from './ui/Button';
import supabase from '../supabase/supabase';
import NotificationBell from './NotificationBell';

const LIGHT_LOGO_URL = "https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1780696106/Remove_Background_wepspn.png";
const DARK_LOGO_URL = "https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1780697986/Blackbackground_no_bg_gyy1me.png";

export default function DashboardLayout({ children, role = 'barber', navigation }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState({ full_name: 'Loading...', avatar_url: null });

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      const { data: profile } = await supabase
        .from('profiles_1740423033529')
        .select('*')
        .eq('id', user.id)
        .single();

      setUserProfile({
        full_name: profile?.full_name || user.email.split('@')[0],
        avatar_url: profile?.avatar_url
      });
    };
    fetchProfile();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      // Ensure we go to login page
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
      navigate('/login', { replace: true });
    }
  };

  const handleLogoClick = () => {
    if (role === 'admin') navigate('/admin/dashboard');
    else if (role === 'barber') navigate('/barber/dashboard');
    else navigate('/app');
  };

  return (
    <div className="min-h-screen bg-brand-light flex">
      {/* Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-brand-dark/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <aside className={cn(
        "w-64 bg-brand-dark text-white min-h-screen flex flex-col fixed z-50 transition-transform duration-300 lg:translate-x-0 h-[100dvh]",
        isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="p-6 flex-1 overflow-y-auto">
          <div className="flex flex-col items-start gap-2 mb-10 cursor-pointer group" onClick={handleLogoClick}>
            <img src={DARK_LOGO_URL} alt="BookOTG" className="h-16 w-auto object-contain" />
            <span className="text-[9px] text-gray-400 uppercase tracking-[0.2em] font-black">{role} Panel</span>
          </div>

          <div className="flex items-center gap-3 p-4 bg-white/5 rounded-2xl mb-8 border border-white/10">
            <img 
              src={userProfile.avatar_url || `https://ui-avatars.com/api/?name=${userProfile.full_name}&background=0047FF&color=fff`} 
              className="w-10 h-10 rounded-full object-cover border-2 border-brand-primary" 
              alt="" 
            />
            <div className="overflow-hidden">
              <p className="font-bold text-xs truncate">{userProfile.full_name}</p>
              <p className="text-[8px] text-brand-accent font-black uppercase tracking-widest leading-none mt-1">{role}</p>
            </div>
          </div>

          <nav className="space-y-1.5">
            {navigation.map((item) => {
              const isActive = location.pathname.includes(item.path);
              return (
                <button
                  key={item.name}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-black transition-all",
                    isActive 
                      ? "bg-brand-primary text-white shadow-lg shadow-brand-primary/20" 
                      : "text-gray-400 hover:bg-white/5 hover:text-white"
                  )}
                >
                  <SafeIcon name={item.icon} className={cn("text-lg", isActive ? "text-white" : "text-gray-400")} />
                  {item.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6 border-t border-white/5">
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-black text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <SafeIcon name="LogOut" className="text-lg" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 p-4 lg:p-8 w-full min-h-screen">
        {/* Top Header */}
        <div className="flex items-center justify-between mb-6 bg-white p-3 px-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100"
            >
              <SafeIcon name="Menu" className="text-2xl text-brand-dark" />
            </button>
            <div className="lg:hidden">
              <img src={LIGHT_LOGO_URL} alt="BookOTG" className="h-10 w-auto object-contain" />
            </div>
          </div>
          
          <div className="hidden lg:block">
            <h4 className="font-black text-brand-dark capitalize tracking-tight">{location.pathname.split('/').pop() || 'Dashboard'}</h4>
            <p className="text-[9px] text-gray-400 font-black uppercase tracking-[0.2em]">BookOTG Sri Lanka</p>
          </div>

          <div className="flex items-center gap-4">
            <NotificationBell light={true} />
            <img 
              src={userProfile.avatar_url || `https://ui-avatars.com/api/?name=${userProfile.full_name}&background=0047FF&color=fff`} 
              className="w-10 h-10 rounded-full border border-gray-200 object-cover hidden sm:block" 
              alt="" 
            />
          </div>
        </div>

        {children}
      </main>
    </div>
  );
}